import { type QueryStatus } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';

/**
 * Fetches the most recent critical demonstrated or resolved risk "First Seen" date per customer.
 *
 * Uses the `created` timestamp (when vulnerability was first discovered) as the "Last Compromise" date,
 * NOT the `updated` timestamp (which changes whenever risk status is modified).
 *
 * Makes two separate GraphQuery calls (each using the proven working format):
 * - Query 1: status STARTS WITH 'O' (Demonstrated) + priority = 0 (Critical)
 * - Query 2: status STARTS WITH 'R' (Resolved/Remediated) + priority = 0 (Critical)
 *
 * Combining into two queries avoids the multi-value STARTS WITH issue.
 *
 * @returns dataMap: Map<email, ISO_timestamp> for O(1) lookup per customer.
 *          The timestamp represents the most recent `created` (First Seen) date across all critical risks.
 */
export function useLastCompromiseDate() {
  const demonstratedQuery = useMy(
    {
      resource: 'risk',
      query: {
        node: {
          labels: ['Risk'],
          filters: [
            {
              field: 'status',
              operator: 'STARTS WITH',
              value: ['O'], // Demonstrated/Opened
            },
            {
              field: 'priority',
              operator: '=',
              value: [0], // 0 = Critical severity
            },
          ],
        },
        limit: 10000, // Max backend limit — prevents pagination truncating results
      },
      allTenants: true,
    },
    {
      doNotImpersonate: true,
    }
  );

  const resolvedQuery = useMy(
    {
      resource: 'risk',
      query: {
        node: {
          labels: ['Risk'],
          filters: [
            {
              field: 'status',
              operator: 'STARTS WITH',
              value: ['RC'], // 'RC' = Resolved/Remediated + Critical (compound status)
              // No priority filter needed — 'RC' already encodes Critical severity.
              // Resolved risks may not retain priority=0 after remediation.
            },
          ],
        },
        limit: 10000, // Max backend limit — prevents pagination truncating results
      },
      allTenants: true,
    },
    {
      doNotImpersonate: true,
    }
  );

  const dataMap = useMemo(() => {
    const map = new Map<string, string>();
    const allRisks = [
      ...(demonstratedQuery.data ?? []),
      ...(resolvedQuery.data ?? []),
    ];

    for (const risk of allRisks) {
      const email = risk.username;
      const timestamp = risk.created;
      if (!timestamp) continue;

      const existing = map.get(email);
      if (!existing || timestamp > existing) {
        map.set(email, timestamp);
      }
    }
    return map;
  }, [demonstratedQuery.data, resolvedQuery.data]);

  const status: QueryStatus =
    demonstratedQuery.status === 'pending' || resolvedQuery.status === 'pending'
      ? 'pending'
      : demonstratedQuery.status === 'error' || resolvedQuery.status === 'error'
        ? 'error'
        : 'success';

  return {
    ...demonstratedQuery,
    status,
    dataMap,
  };
}
